import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, MapPin, Stethoscope, Brain, Heart, Eye, Bone, Shield, Clock, Star, Filter, Sparkles, ArrowRight } from 'lucide-react';
import { useSearch } from '../../hooks/useSearch';
import './SmartSearch.css';

const SmartSearch = () => {
  const navigate = useNavigate();
  const {
    searchCategory,
    handleCategoryChange,
    handleSuggestionSelect,
    getSpecialtyFromSymptom
  } = useSearch();

  const [selectedBodyPart, setSelectedBodyPart] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchStats] = useState({
    totalSearches: 125000,
    doctorsFound: 2500,
    avgResponseTime: 2.3
  });
  const [isAnimating, setIsAnimating] = useState(false);

  // Enhanced search categories
  const searchCategories = [
    { id: 'symptoms', label: 'By Symptoms', icon: Stethoscope, description: 'Search by what\'s bothering you', color: 'var(--primary-teal)' },
    { id: 'specialty', label: 'By Specialty', icon: Brain, description: 'Find doctors by medical specialty', color: 'var(--info-coral)' },
    { id: 'doctor', label: 'By Doctor Name', icon: User, description: 'Search for specific doctors', color: 'var(--success-lime)' },
    { id: 'location', label: 'By Location', icon: MapPin, description: 'Find doctors near you', color: 'var(--primary-navy)' }
  ];

  // Enhanced body parts with better descriptions
  const bodyParts = [
    { name: 'Head & Brain', icon: Brain, color: '#3B82F6', symptoms: ['Headache', 'Migraine', 'Memory issues'] },
    { name: 'Heart & Chest', icon: Heart, color: '#EF4444', symptoms: ['Chest pain', 'Heart palpitations', 'Shortness of breath'] },
    { name: 'Eyes', icon: Eye, color: '#8B5CF6', symptoms: ['Vision problems', 'Eye pain', 'Blurred vision'] },
    { name: 'Bones & Joints', icon: Bone, color: '#F59E0B', symptoms: ['Joint pain', 'Back pain', 'Fractures'] },
    { name: 'General Health', icon: Shield, color: '#10B981', symptoms: ['Fever', 'Fatigue', 'General checkup'] }
  ];

  // Enhanced symptoms with categories
  const popularSymptoms = [
    { text: 'Headache', category: 'Neurology', urgency: 'low' },
    { text: 'Chest pain', category: 'Cardiology', urgency: 'high' },
    { text: 'Fever', category: 'General Medicine', urgency: 'medium' },
    { text: 'Back pain', category: 'Orthopedics', urgency: 'low' },
    { text: 'Stomach ache', category: 'Gastroenterology', urgency: 'medium' },
    { text: 'Shortness of breath', category: 'Pulmonology', urgency: 'high' },
    { text: 'Skin rash', category: 'Dermatology', urgency: 'low' },
    { text: 'Dizziness', category: 'Neurology', urgency: 'medium' }
  ];

  const additionalSymptoms = [
    { text: 'Nausea', category: 'Gastroenterology', urgency: 'medium' },
    { text: 'Joint pain', category: 'Orthopedics', urgency: 'low' },
    { text: 'Vision problems', category: 'Ophthalmology', urgency: 'medium' },
    { text: 'Hearing loss', category: 'ENT', urgency: 'low' },
    { text: 'Sleep problems', category: 'Sleep Medicine', urgency: 'low' },
    { text: 'Anxiety', category: 'Psychiatry', urgency: 'medium' }
  ];

  const allSymptoms = [...popularSymptoms, ...additionalSymptoms];

  // Enhanced filters
  const filters = [
    { id: 'all', label: 'All', icon: Filter },
    { id: 'urgent', label: 'Urgent', icon: Clock },
    { id: 'nearby', label: 'Nearby', icon: MapPin },
    { id: 'top-rated', label: 'Top Rated', icon: Star }
  ];

  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 600);
    return () => clearTimeout(timer);
  }, [selectedBodyPart]);

  const handleBodyPartClick = (bodyPart) => {
    setSelectedBodyPart(bodyPart);
    setIsAnimating(true);
    setTimeout(() => {
      navigate('/find-doctors', { 
        state: { 
          searchQuery: bodyPart.name,
          category: 'body-part',
          symptoms: bodyPart.symptoms 
        }
      });
    }, 300);
  };

  const handleSymptomClick = (symptom) => {
    setIsAnimating(true);
    setTimeout(() => {
      handleSuggestionSelect(symptom.text);
      navigate('/find-doctors', { 
        state: { 
          searchQuery: symptom.text,
          category: 'symptoms',
          specialty: getSpecialtyFromSymptom(symptom.text)
        }
      });
    }, 300);
  };

  const handleCategoryClick = (category) => {
    setIsAnimating(true);
    setTimeout(() => {
      handleCategoryChange(category.id);
      navigate('/find-doctors', { 
        state: { 
          searchQuery: category.label,
          category: category.id
        }
      });
    }, 300);
  };

  const handleFilterClick = (filterId) => {
    setSelectedFilter(filterId);
  };

  return (
    <section className="smart-search">
      <div className="container">
        {/* Header */}
        <div className="search-header">
          <div className="search-badge">
            <Sparkles size={16} />
            <span>Smart Search</span>
          </div>
          <h2>Find the Right Care for You</h2>
          <p>Our intelligent search helps you find the perfect doctor based on your needs</p>
        </div>

        {/* Search Categories */}
        <div className="search-categories">
          {searchCategories.map((category) => {
            const IconComponent = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => handleCategoryClick(category)}
                className={`category-tab ${searchCategory === category.id ? 'active' : ''}`}
                style={{ '--category-color': category.color }}
              >
                <IconComponent size={20} className="category-icon" />
                <div className="category-content">
                  <span className="category-label">{category.label}</span>
                  <span className="category-description">{category.description}</span>
                </div>
                <ArrowRight size={16} className="category-arrow" />
              </button>
            );
          })}
        </div>

        {/* Search Filters */}
        <div className="search-filters">
          {filters.map((filter) => {
            const IconComponent = filter.icon;
            return (
              <button
                key={filter.id}
                onClick={() => handleFilterClick(filter.id)}
                className={`filter-chip ${selectedFilter === filter.id ? 'active' : ''}`}
              >
                <IconComponent size={18} className="filter-chip-icon" />
                <span className="filter-chip-text">{filter.label}</span>
              </button>
            );
          })}
        </div>

        {/* Body Map */}
        <div className="body-map-section">
          <h3>Select a Body Part</h3>
          <div className="body-map">
            {bodyParts.map((part, index) => {
              const IconComponent = part.icon;
              return (
                <button
                  key={index}
                  onClick={() => handleBodyPartClick(part)}
                  className={`body-part ${selectedBodyPart?.name === part.name ? 'selected' : ''} ${isAnimating ? 'animating' : ''}`}
                  style={{ '--part-color': part.color }}
                >
                  <IconComponent size={32} className="body-part-icon" />
                  <span className="body-part-name">{part.name}</span>
                  <div className="body-part-symptoms">
                    {part.symptoms.slice(0, 2).map((symptom, idx) => (
                      <span key={idx} className="symptom-tag">{symptom}</span>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Popular Symptoms */}
        <div className="symptoms-section">
          <h3>Popular Symptoms</h3>
          <div className="suggestions">
            {allSymptoms.map((symptom, index) => (
              <button
                key={index}
                onClick={() => handleSymptomClick(symptom)}
                className={`suggestion-tag ${isAnimating ? 'animating' : ''} ${symptom.urgency}`}
              >
                <Stethoscope size={16} />
                <span className="symptom-text">{symptom.text}</span>
                <span className="symptom-category">{symptom.category}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Search Statistics */}
        <div className="search-stats">
          <h3>Trusted by Thousands</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-icon">
                <Search size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{searchStats.totalSearches.toLocaleString()}+</div>
                <div className="stat-label">Successful Searches</div>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">
                <User size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{searchStats.doctorsFound.toLocaleString()}+</div>
                <div className="stat-label">Expert Doctors</div>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">
                <Clock size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{searchStats.avgResponseTime}s</div>
                <div className="stat-label">Average Response</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SmartSearch;